import React, { useEffect, useState } from 'react';
import Localize from 'react-intl-universal';

import { isEmpty } from 'lodash-es';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import NoContent from '@components/NoContentComponent/NoContent';

const BookingOverview = ({ data, bookingData, fetchBilling }) => {
  const [rows, setRows] = useState([]);
  const [sessionColumns, setSessionColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (isEmpty(bookingData)) {
        setIsLoading(true);
        await fetchBilling();
        setIsLoading(false);
      }
    };
    loadData();
  }, [bookingData, fetchBilling]);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const eventName = bookingData?.event?.name || 'Event';

    const sessionMap = new Map();

    data.forEach((participant) => {
      const sessions = participant.sessionParticipants || [];
      sessions.forEach((sp) => {
        const { name, optionalSession } = sp.session;
        if (!sessionMap.has(name)) {
          sessionMap.set(name, optionalSession);
        }
      });
    });

    const sessionHeaders = Array.from(sessionMap.entries()).map(([name, optional]) => ({
      name,
      optionalSession: optional
    }));
    setSessionColumns([{ name: eventName, isEvent: true }, ...sessionHeaders]);

    // Build rows
    const formattedRows = data.map((participant) => {
      const name = `${participant.attendee.firstName} ${participant.attendee.lastName}`;
      const eventStatus = participant.attendee.status?.value || '';
      const sessionsMap = {};
      (participant.sessionParticipants || []).forEach((sp) => {
        sessionsMap[sp.session.name] = sp.status.value;
      });

      return {
        participant: name,
        [eventName]: eventStatus,
        ...sessionsMap
      };
    });

    setRows(formattedRows);
  }, [data]);

  if (isLoading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' height={200}>
        <CircularProgress />
      </Box>
    );
  }

  return sessionColumns?.length && rows?.length ? (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ border: '1px solid #ccc', fontWeight: 'bold' }}>
              <strong>{Localize.get('Booking.participant')}</strong>
            </TableCell>
            {sessionColumns.map((column, index) => (
              <TableCell key={index} sx={{ border: '1px solid #ccc', fontWeight: 'bold' }}>
                <strong>
                  {column.isEvent ? Localize.get('Booking.event') : Localize.get('Booking.session')}
                  {'-'}
                  {column.name}{' '}
                  {!column.isEvent &&
                    (column.optionalSession === false
                      ? `(${Localize.get('Booking.mandatory')})`
                      : `(${Localize.get('Booking.optional')})`)}
                </strong>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow
              key={i}
              sx={(theme) => ({
                '&:hover': {
                  backgroundColor: theme.palette.booking.backgroundField,
                  cursor: 'pointer'
                }
              })}
            >
              <TableCell sx={{ border: '1px solid #ccc' }}>{row.participant}</TableCell>
              {sessionColumns.map((col, j) => (
                <TableCell key={j} sx={{ border: '1px solid #ccc' }}>
                  {row[col.name] || ''}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  ) : (
    <Box data-testid={'bookingOverviewTableNoContent'}>
      <NoContent
        infoType={Localize.get('EventDetails.noBookingParticipants')}
        infoMessage={Localize.get('EventDetails.selectNewBooking')}
      />
    </Box>
  );
};

export default BookingOverview;
